import { FC, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "redux/hooks";

import { useMediaQuery } from "context/media/MediaContext";
import { DIGIPOSTHEME } from "theme/digiPosTheme";
import {
  Container,
  Panel,
  PanelHeader,
  Button,
  Typography,
  RadioButtonSingle,
} from "components";
import {
  Col,
  ElementWrapper,
  Row,
  StyledFormWrapper,
  StyledOverFlow,
} from "components/BaseComponents/Flex";
import { merchantAsync } from "redux/actions/Merchant.action";
import {
  resetMerchant,
  sendMerchant,
  setMerchantError,
} from "redux/slices/MerchantSlice";
import { merchantSwitchSuccessAsync } from "redux/actions/MerchantSwitch.action";
import { IMerchantType } from "models/Login/choiceMerchant.model";
import { ResetLogin } from "redux/slices/LoginSlice";
import { resetOtp } from "redux/slices/OtpSlice";
import { resetMenu, setMenuClick } from "redux/slices/StepSlice";

const ChoiceMerchant: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMediaQuery();
  const dispatch = useAppDispatch();
  const { merchant, selectedMerchant } = useAppSelector(
    (state) => state.merchant
  );

  const { previousMenuClickId } = useAppSelector((state) => state.step);

  let fromOtherTransactionChangeMerchant =
    location.state?.is_merchant_change || false;
  const [isMerchantChange, setIsMerchantChange] = useState<boolean>(
    fromOtherTransactionChangeMerchant
  );
  const [selectedClick, setSelectedClick] = useState("");

  useEffect(() => {
    dispatch(merchantAsync());
  }, []);

  const { handleSubmit } = useForm({
    defaultValues: {
      selectedMerchant: selectedClick || merchant?.DefaultMerchantNo!,
    },
  });
  const onSubmit = (data: any) => {
    const datas = selectedClick || merchant?.DefaultMerchantNo!;
    dispatch(sendMerchant(datas));
    setIsMerchantChange(false);
  };

  const selectRadio = (val: string) => {
    setSelectedClick(val);
  };

  useEffect(() => {
    if (!isMerchantChange && selectedMerchant) {
      dispatch(merchantSwitchSuccessAsync());
      navigate("/");
    }
  }, [selectedMerchant, isMerchantChange, dispatch, navigate]);

  useEffect(() => {
    if (merchant && merchant.Merchants?.length === 1) {
      setSelectedClick(merchant.Merchants[0].No);
      const singleMerchant = selectedClick || merchant?.DefaultMerchantNo!;
      dispatch(sendMerchant(singleMerchant));
      setIsMerchantChange(false);
    } else if (merchant && !merchant?.Merchants) {
      dispatch(setMerchantError("MRC#E-Belgeli işyeriniz bulunmamaktadır."));
    }
  }, [merchant, dispatch, navigate, selectedClick]);

  const MerchantPageExit = () => {
    dispatch(ResetLogin());
    dispatch(resetOtp());
    dispatch(resetMerchant());
    dispatch(resetMenu());

    navigate("/");
  };

  const goBack = () => {
    dispatch(setMenuClick(previousMenuClickId));
    navigate(-1);
  };

  return (
    <Container>
      <ElementWrapper minHeight='calc(100vh - 137px)' width='872px'>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Panel width='646px' marginTop='20px'>
            <PanelHeader
              title={t("merchant_select_title").toString()}
              backgroundColor={DIGIPOSTHEME.colors.footerGrey}
            />
            <StyledFormWrapper>
              <StyledOverFlow maxHeight='600px'>
                {merchant &&
                  merchant?.Merchants?.map(
                    (m: IMerchantType, index: number) => (
                      <Row
                        key={index}
                        onClick={() => selectRadio(m.No)}
                        isCursor={true}
                        borderBottom='1px solid #ccc'
                        padding='0 0 10px 0'
                        disabled={m.Statu === "I" ? true : false}
                      >
                        <Col>
                          <Typography
                            width='250px'
                            margin='5px 15px'
                            size='14px'
                            color={
                              m.Statu === "A"
                                ? DIGIPOSTHEME.colors.primaryToggleGreen
                                : DIGIPOSTHEME.colors.headerRed
                            }
                          >
                            {m.Statu === "A" ? t("open") : t("close")}
                          </Typography>
                          <Typography
                            width='250px'
                            margin={isMobile ? "auto auto auto 0" : "0 15px"}
                            size={isMobile ? "15px" : "18px"}
                            display={isMobile ? "flex" : "contents"}
                          >
                            {m.Name}
                          </Typography>
                          <Typography
                            width='250px'
                            margin='5px 15px'
                            size='12px'
                            color={DIGIPOSTHEME.colors.secondaryGraySecondary}
                          >
                            {t("marchant_no")}: {m.No}
                          </Typography>
                        </Col>
                        <Col>
                          <RadioButtonSingle
                            name='selectedMerchant'
                            value={m.No}
                            defaultValue={merchant?.DefaultMerchantNo!}
                            selectedClick={selectedClick}
                            disabled={m.Statu !== "A"}
                          />
                        </Col>
                      </Row>
                    )
                  )}
              </StyledOverFlow>

              <Row
                justifyContent='flex-end'
                padding='32px 20px 10px 20px'
                isResponsive
                noMargin
                gap
              >
                <Col>
                  <Button
                    type='button'
                    width={isMobile ? "100%" : "100px"}
                    text={
                      !fromOtherTransactionChangeMerchant
                        ? t("exit").toString()
                        : `← ${t("back")}`
                    }
                    onClick={() =>
                      !fromOtherTransactionChangeMerchant
                        ? MerchantPageExit()
                        : goBack()
                    }
                    color='light'
                  />
                </Col>

                <Col offSet={22} size={0.5}>
                  <Button text={t("continue").toString()} type='submit' />
                </Col>
              </Row>
            </StyledFormWrapper>
          </Panel>
        </form>
      </ElementWrapper>
    </Container>
  );
};

export default ChoiceMerchant;
function previousMenuClickId(
  previousMenuClickId: any
): { payload: number; type: "step/setMenuClick" } {
  throw new Error("Function not implemented.");
}
